import { CheckIcon, UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SlotItem } from "../utils";
import { formatDueTime, formatRelative, isWindowOpen, minutesUntil } from "../utils";
import { useSlotMutations } from "../hooks/useSlotMutations";

export function NowCard({ item, next, now }: { item: SlotItem; next: SlotItem | null; now: Date }) {
  const { complete, skip, isLoading } = useSlotMutations(item.goalId, item.slot.id);

  const open = isWindowOpen(item, now);
  const until = minutesUntil(item.slot.dueTimeLocal, now);
  const dueLabel = open
    ? until >= 0
      ? `Due ${formatDueTime(item.slot.dueTimeLocal)} · window open`
      : `Due ${formatDueTime(item.slot.dueTimeLocal)} · still in grace`
    : `Due at ${formatDueTime(item.slot.dueTimeLocal)} · ${formatRelative(until)}`;

  return (
    <div
      key={item.slot.id}
      className="rounded-2xl border border-primary/30 border-l-4 border-l-primary bg-accent/40 px-5 py-4 animate-in fade-in duration-300"
    >
      <p className="text-[11px] font-medium uppercase tracking-widest text-primary mb-1.5">
        {open ? "Now" : "Up next"}
      </p>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground truncate">
        {item.goalName}
      </p>
      <h2 className="text-lg font-semibold leading-snug mt-0.5">{item.slot.name}</h2>
      <p className="text-xs text-muted-foreground tabular-nums mt-1">{dueLabel}</p>
      {item.notifyCircleName && (
        <p className="flex items-center gap-1 text-[11px] text-muted-foreground/80 mt-1">
          <UsersIcon className="w-3 h-3 shrink-0" />
          shared with {item.notifyCircleName}
        </p>
      )}

      <div className="flex items-center gap-3 mt-4">
        <Button
          size="lg"
          className="flex-1"
          disabled={isLoading}
          onClick={() => complete.mutate()}
        >
          <CheckIcon strokeWidth={3} />
          Mark done
        </Button>
        <button
          onClick={() => skip.mutate()}
          disabled={isLoading}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-2 rounded-lg hover:bg-muted disabled:opacity-40"
        >
          Skip
        </button>
      </div>

      {next && (
        <p className="text-xs text-muted-foreground mt-3 truncate">
          then: {next.slot.name} ·{" "}
          <span className="tabular-nums">{formatDueTime(next.slot.dueTimeLocal)}</span>
        </p>
      )}
    </div>
  );
}

export function AllDoneCard({ skippedOnly }: { skippedOnly: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-accent/40 px-5 py-5 flex items-center gap-3">
      <span className="shrink-0 w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
        <CheckIcon className="w-4 h-4 text-primary" strokeWidth={3} />
      </span>
      <div>
        <p className="text-sm font-semibold">
          {skippedOnly ? "Day closed out." : "That's everything for today."}
        </p>
        <p className="text-xs text-muted-foreground">Nothing left to decide.</p>
      </div>
    </div>
  );
}
